import { motion } from "framer-motion";
import React from "react";
import { EventCategory } from "../types/calendar.types";

interface CategoryListProps {
  categories: EventCategory[];
  activeCategory: string | null;
  onCategoryClick: (categoryId: string) => void;
  onEditCategory?: (category: EventCategory) => void;
  isAdmin: boolean;
  onAddCategory?: () => void;
}

const getCategoryColorIndicator = (colorValue: string): string => {
  switch (colorValue) {
    case "sky":
      return "bg-sky-500";
    case "indigo":
      return "bg-indigo-500";
    case "yellow":
      return "bg-yellow-500";
    case "green":
      return "bg-green-500";
    case "red":
      return "bg-red-500";
    case "purple":
      return "bg-purple-500";
    case "orange":
      return "bg-orange-500";
    case "pink":
      return "bg-pink-500";
    case "teal":
      return "bg-teal-500";
    default:
      return "bg-gray-500";
  }
};

const CategoryList: React.FC<CategoryListProps> = ({
  categories,
  activeCategory,
  onCategoryClick,
  onEditCategory,
  isAdmin,
  onAddCategory,
}) => {
  return (
    <ul className="flex flex-wrap items-center -m-1">
      {categories.map((category) => (
        <li className="m-1" key={category.id}>
          <div className="relative group">
            <motion.button
              className={`btn-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-500 dark:text-gray-400 ${
                activeCategory === category.id
                  ? "ring-2 ring-offset-2 ring-offset-white dark:ring-offset-gray-900 ring-blue-500"
                  : ""
              }`}
              onClick={() => onCategoryClick(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div
                className={`w-1 h-3.5 shrink-0 ${getCategoryColorIndicator(
                  category.color
                )}`}
              ></div>
              <span className="ml-1.5">{category.name}</span>
            </motion.button>

            {isAdmin && onEditCategory && (
              <button
                className="absolute -right-2 -top-2 bg-white dark:bg-gray-800 rounded-full p-1 shadow-sm border border-gray-200 dark:border-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditCategory(category);
                }}
              >
                <svg
                  className="w-3 h-3 fill-current text-gray-500"
                  viewBox="0 0 16 16"
                >
                  <path d="M11.7.3c-.4-.4-1-.4-1.4 0l-10 10c-.2.2-.3.4-.3.7v4c0 .6.4 1 1 1h4c.3 0 .5-.1.7-.3l10-10c.4-.4.4-1 0-1.4l-4-4zM4.6 14H2v-2.6l6-6L10.6 8l-6 6zM12 6.6L9.4 4 11 2.4 13.6 5 12 6.6z" />
                </svg>
              </button>
            )}
          </div>
        </li>
      ))}

      {isAdmin && onAddCategory && (
        <li className="m-1">
          <motion.button
            className="btn-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-violet-500"
            onClick={onAddCategory}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            +Add New
          </motion.button>
        </li>
      )}
    </ul>
  );
};

export default CategoryList;
